import { useEffect, useMemo, useRef, useState } from 'react';
import Card from './Card';
import { CategoryInterface } from '../../../services/exports/Interfaces';
import SideBarIcon from '../../../assets/logo/SideBarIcon';
import useTheme from '../../../hooks/ui/useTheme';
import MenuSideBar from './MenuSideBar';
import useMenu from '../../../hooks/menu/useMenu';
import SearchInput from './SearchInput/SearchInput';
import classNames from 'classnames';
import useScreenType from '../../../hooks/utility/useScreenType';
import ScrollMenu from './ScrollMenu';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '../../../assets/icons/actions/SearchIcon';

interface Props {
  focusedCategoryId?: number;
  categories?: CategoryInterface[];
}

const MenuBar = ({ focusedCategoryId, categories }: Props) => {
  const theme = useTheme();
  const { menus, selectedMenu } = useMenu();

  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [showSideBar, setShowSideBar] = useState<boolean>(false);
  const { isMobile } = useScreenType();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSearchInput = !isMobile || (isMobile && searchParams.has('search'));
  const hasCategories = !!categories?.length && (!isMobile || (isMobile && !searchParams.has('search')));
  const menuBarRef = useRef<HTMLDivElement>();

  const onClickSearch = (): void => {
    searchParams.append('search', '');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    selectedMenu && setSelectedMenuId(selectedMenu?.id);

    selectedMenuId &&
      selectedMenuId !== selectedMenu?.id &&
      menus.firstWhere('id', selectedMenuId) &&
      categories?.length > 0 &&
      jumpToCategory(categories[0].id);
  }, [selectedMenu?.id]);

  function toggleSideBar(): void {
    setShowSideBar(!showSideBar);
  }

  function jumpToCategory(id?: number): void {
    const category = document.getElementById(`category-id-${id}`);
    const elementPosition = category.getBoundingClientRect().top;
    const top = elementPosition + window.scrollY - menuBarRef.current.scrollHeight;

    window.scrollTo({
      left: 0,
      top,
      behavior: 'smooth',
    });
  }

  const renderSideBar = useMemo<JSX.Element>(() => {
    return (
      <MenuSideBar
        categories={categories}
        open={showSideBar}
        toggle={setShowSideBar}
        focusedCategoryId={focusedCategoryId}
        onSelectCategory={(category) => jumpToCategory(category?.id)}
      />
    );
  }, [showSideBar, focusedCategoryId]);

  const renderMenuButton = (): JSX.Element => (
    <button
      className={classNames('flex flex-row p-2xs bg-white shadow-icon rounded-[4px] items-center mr-md shrink-0', {
        'rounded-full size-8 border mx-md justify-center': isMobile,
      })}
      onClick={toggleSideBar}
      id={'menu-button'}
    >
      <SideBarIcon color={theme.color.gray['300']} className="h-4 w-4" />
    </button>
  );

  return (
    <div className="bg-white h-fit" id="menu-bar" ref={menuBarRef}>
      {hasSearchInput && (
        <SearchInput className={classNames({ 'px-md': isMobile }, { 'pb-md': !isMobile && !hasCategories })} />
      )}
      {hasCategories && (
        <div className="px-md h-full w-full flex flex-row items-center relative">
          {isMobile && (
            <button
              className="size-8 border rounded-full flex justify-center items-center shrink-0 shadow-icon"
              onClick={onClickSearch}
            >
              <SearchIcon className="size-4" />
            </button>
          )}
          {renderMenuButton()}
          {categories?.length > 0 && (
            <ScrollMenu focusedCategoryId={focusedCategoryId} jumpToCategory={jumpToCategory}>
              {categories?.map((category: CategoryInterface) => (
                <Card
                  key={category?.id?.toString()}
                  itemId={category?.id?.toString()}
                  category={category}
                  focusedCategoryId={focusedCategoryId}
                  onClick={jumpToCategory}
                />
              ))}
            </ScrollMenu>
          )}
        </div>
      )}
      <hr className="shadow-[0_2px_4px_0px_#A7BAC54D]" />
      {renderSideBar}
    </div>
  );
};

export default MenuBar;
